import axios from "axios";
import { useEffect, useState } from "react";
import { Calendar2Week } from "react-bootstrap-icons";

import { ApiConnectionString } from "../../../constants/ApiConntection";
import LoadingSpinner from "../../../components/LoadingSpinner";

import SettingsCard from "./SettingsCard";
import SectionMessage from "./SectionMessage";

import {
  createEmptyMessage,
  getErrorMessage,
} from "../settingsUtils";

const days = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const emptyAvailability = {
  mondayAvailable: false,
  mondayStartTime: "",
  mondayEndTime: "",

  tuesdayAvailable: false,
  tuesdayStartTime: "",
  tuesdayEndTime: "",

  wednesdayAvailable: false,
  wednesdayStartTime: "",
  wednesdayEndTime: "",

  thursdayAvailable: false,
  thursdayStartTime: "",
  thursdayEndTime: "",

  fridayAvailable: false,
  fridayStartTime: "",
  fridayEndTime: "",

  saturdayAvailable: false,
  saturdayStartTime: "",
  saturdayEndTime: "",

  sundayAvailable: false,
  sundayStartTime: "",
  sundayEndTime: "",
};

const formatTime = (value) => {
  if (!value || typeof value !== "string") {
    return "";
  }

  return value.substring(0, 5);
};

const getApiValue = (data, key) => {
  const pascalCaseKey =
    key.charAt(0).toUpperCase() + key.slice(1);

  return data?.[key] ?? data?.[pascalCaseKey];
};

export default function AvailabilitySettings({ token }) {
  const [availability, setAvailability] =
    useState(emptyAvailability);

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const [message, setMessage] =
    useState(createEmptyMessage);

  useEffect(() => {
    const getAvailability = async () => {
      if (!token) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setMessage(createEmptyMessage());

        const response = await axios.get(
          `${ApiConnectionString}/api/Availablity`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const data = response.data || {};
        const formattedAvailability = {
          ...emptyAvailability,
        };

        Object.keys(emptyAvailability).forEach((key) => {
          const value = getApiValue(data, key);

          formattedAvailability[key] = key.includes("Time")
            ? formatTime(value)
            : value ?? emptyAvailability[key];
        });

        setAvailability(formattedAvailability);
      } catch (error) {
        console.error(error);

        setMessage({
          error: getErrorMessage(
            error,
            "Failed to load weekly availability."
          ),
          success: "",
        });
      } finally {
        setIsLoading(false);
      }
    };

    getAvailability();
  }, [token]);

  const updateField = (field, value) => {
    setAvailability((current) => ({
      ...current,
      [field]: value,
    }));

    setMessage(createEmptyMessage());
  };

  const validateAvailability = () => {
    for (const day of days) {
      const dayKey = day.toLowerCase();

      const availableKey = `${dayKey}Available`;
      const startKey = `${dayKey}StartTime`;
      const endKey = `${dayKey}EndTime`;

      if (!availability[availableKey]) {
        continue;
      }

      if (
        !availability[startKey] ||
        !availability[endKey]
      ) {
        return `Please enter a start and finish time for ${day}.`;
      }

      if (
        availability[startKey] >= availability[endKey]
      ) {
        return `${day}'s finish time must be later than its start time.`;
      }
    }

    return "";
  };

  const buildPayload = () => {
    const payload = {};

    days.forEach((day) => {
      const dayKey = day.toLowerCase();

      const availableKey = `${dayKey}Available`;
      const startKey = `${dayKey}StartTime`;
      const endKey = `${dayKey}EndTime`;

      const isAvailable = availability[availableKey];

      payload[availableKey] = isAvailable;

      payload[startKey] = isAvailable
        ? `${availability[startKey]}:00`
        : null;

      payload[endKey] = isAvailable
        ? `${availability[endKey]}:00`
        : null;
    });

    return payload;
  };

  const saveAvailability = async (event) => {
    event.preventDefault();

    const validationError = validateAvailability();

    if (validationError) {
      setMessage({
        error: validationError,
        success: "",
      });

      return;
    }

    try {
      setIsSaving(true);
      setMessage(createEmptyMessage());

      await axios.put(
        `${ApiConnectionString}/api/Availablity`,
        buildPayload(),
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setMessage({
        error: "",
        success: "Weekly availability saved successfully.",
      });
    } catch (error) {
      console.error(error);

      setMessage({
        error: getErrorMessage(
          error,
          "Failed to save weekly availability."
        ),
        success: "",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <SettingsCard
      icon={<Calendar2Week />}
      title="Weekly availability"
      description="Choose which days you work and your usual working hours."
    >
      <div className="settings-form availability-settings">
        {isLoading ? (
          <div className="availability-settings-loading">
            <LoadingSpinner />
          </div>
        ) : (
          <form
            className="settings-form"
            onSubmit={saveAvailability}
          >
            <SectionMessage message={message} />

            <div className="availability-list">
              {days.map((day) => {
                const dayKey = day.toLowerCase();

                const availableKey =
                  `${dayKey}Available`;

                const startKey =
                  `${dayKey}StartTime`;

                const endKey =
                  `${dayKey}EndTime`;

                const isAvailable =
                  availability[availableKey];

                return (
                  <div
                    className="availability-row"
                    key={day}
                  >
                    <div className="availability-day">
                      <strong>{day}</strong>

                      <span>
                        {isAvailable
                          ? "Available"
                          : "Closed"}
                      </span>
                    </div>

                    <label
                      className="availability-toggle"
                      aria-label={`Toggle ${day} availability`}
                    >
                      <input
                        type="checkbox"
                        checked={isAvailable}
                        onChange={(event) =>
                          updateField(
                            availableKey,
                            event.target.checked
                          )
                        }
                      />

                      <span />
                    </label>

                    <div className="availability-times">
                      <input
                        type="time"
                        className="form-control"
                        aria-label={`${day} start time`}
                        value={availability[startKey]}
                        disabled={!isAvailable}
                        required={isAvailable}
                        onChange={(event) =>
                          updateField(
                            startKey,
                            event.target.value
                          )
                        }
                      />

                      <span>to</span>

                      <input
                        type="time"
                        className="form-control"
                        aria-label={`${day} finish time`}
                        value={availability[endKey]}
                        disabled={!isAvailable}
                        required={isAvailable}
                        onChange={(event) =>
                          updateField(
                            endKey,
                            event.target.value
                          )
                        }
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="settings-actions">
              <button
                type="submit"
                disabled={isSaving}
              >
                {isSaving
                  ? "Saving..."
                  : "Save availability"}
              </button>
            </div>
          </form>
        )}
      </div>
    </SettingsCard>
  );
}